import React, { useState, useEffect, useMemo } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import {
  FileText, MagnifyingGlass, DownloadSimple, CaretLeft, CaretRight, CaretUp, CaretDown,
} from '@phosphor-icons/react';

import { getChangeTypes, getUpdateReports } from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { pickerOnly } from '../utils/pickerOnly';
import Toast from './Toast';
import { useToast } from '../utils/useToast';

const PAGE_SIZE_OPTIONS = [12, 24, 50, 100];

// ── helpers ──────────────────────────────────────────────
// One wording for "nothing to show", used by both the toast and the empty state.
// A single-day search says so rather than naming the same date twice.
function noDetailsMessage(from, to) {
  if (!from || !to) return 'There are no details for the selected filters.';
  return from === to
    ? `There are no details for ${formatDatePart(from)}.`
    : `There are no details between ${formatDatePart(from)} and ${formatDatePart(to)}.`;
}

function todayString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toDateString(value) {
  if (!value) return '';
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return String(value).substring(0, 10);
}

function tryParse(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return value; }
}

function formatDatePart(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function formatTimePart(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function asText(value) {
  return typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : (value ?? '');
}

// Deterministic colour per change-type, so the palette scales with whatever
// change types the API returns instead of a fixed list.
const TAG_PALETTE = [
  { bg: '#EFF4FF', border: '#BFDBFE', text: '#1D4ED8' }, // info
  { bg: '#ECFDF5', border: '#A7F3D0', text: '#065F46' }, // success
  { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' }, // warning
  { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' }, // danger
];
function paletteForType(type, allTypes) {
  const known = { UPDATE: 2, DELETE: 3, RECEIPT: 1, MV_CANCEL: 3 };
  const key = String(type || '').toUpperCase();
  if (known[key] !== undefined) return TAG_PALETTE[known[key]];
  const idx = Math.max(0, allTypes.indexOf(type));
  return TAG_PALETTE[idx % TAG_PALETTE.length];
}

const th = {
  textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB',
  color: '#6B7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase',
  letterSpacing: '0.04em', whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827',
  fontSize: '12px', verticalAlign: 'top',
};
const jsonCell = {
  margin: 0, fontFamily: "'Courier New', monospace", fontSize: '11px', color: '#4B5563',
  whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxWidth: '280px', background: '#F9FAFB',
  border: '1px solid #E5E7EB', borderRadius: '6px', padding: '6px 8px', lineHeight: 1.5,
};

// Five screens have written their audit under two different names over time —
// the Angular app used a prose label, the React port uses a SCREAMING_SNAKE
// constant — so one screen's history is split across two entries in the Change
// Type list. Searching either name returned only half the records, and the other
// half looked like it had never been logged. Selecting one now searches both.
//
// vchtype is matched exactly server-side, so this cannot be done with a LIKE;
// and the rows themselves must not be rewritten, since the name is what the
// record was saved as. Each group is one screen.
const CHANGE_TYPE_ALIASES = [
  ['NAME_CHANGE', 'Name Change'],
  ['MV_CANCEL', 'MV Cancel'],
  ['BPO_GUARANTOR_DELETE', 'BPO Guarantor Id Delete'],
  ['CHEQUE_RETURN_CHARGES_PERMISSION', 'Cheque Return Charges Permission'],
  ['PS_SUBSCRIBER_INCOME_CHANGE', 'ps_subscriber_income_CHANGE'],
];

/** Every name the given change type has been written under, the selected one first. */
function typesToQuery(selected) {
  const group = CHANGE_TYPE_ALIASES.find((g) => g.includes(selected));
  if (!group) return [selected];
  return [selected, ...group.filter((t) => t !== selected)];
}

function ReportsOfUpdatesPage() {
  const { toast, setToast, showToast } = useToast();

  const [fromDate, setFromDate] = useState(todayString());
  const [toDate, setToDate] = useState(todayString());
  const [changeType, setChangeType] = useState('');
  const [exportFormat, setExportFormat] = useState('Excel');

  const [changeTypes, setChangeTypes] = useState([]);
  const [changeTypesLoaded, setChangeTypesLoaded] = useState(false);
  const [filteredReports, setFilteredReports] = useState([]);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // The range the loaded results belong to. Held separately from fromDate /
  // toDate so that editing the boxes after a search does not silently re-label
  // the results (or the "no details" message) with a range nobody searched.
  const [searchedRange, setSearchedRange] = useState(null);

  const [sortField, setSortField] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);

  useEffect(() => {
    getChangeTypes()
      .then((data) => {
        const types = (data || []).map((x) => x.changeType).filter(Boolean);
        // "ALL" only makes sense when there is something to fan out over.
        setChangeTypes(types.length ? ['ALL', ...types] : []);
        setChangeTypesLoaded(true);
      })
      .catch((err) => {
        console.error('Error loading change types:', err);
        setChangeTypesLoaded(false);
        showToast('error', 'Load Failed', 'Could not load change types.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTypeOptions = useMemo(() => changeTypes.map((t) => ({ label: t, value: t })), [changeTypes]);

  // The change types come from the audit log itself, so an empty list means
  // nothing has been logged yet — there is no type to pick and no point making
  // the user pick one. The date range alone then runs the search, which
  // legitimately finds nothing and reports "No data found". While the list is
  // still loading, or if loading failed, the field stays required as before.
  const noChangeTypes = changeTypesLoaded && changeTypes.length === 0;
  const changeTypeRequired = !noChangeTypes;

  const isFormValid = !!fromDate && !!toDate && (noChangeTypes || !!changeType);

  const filterReports = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (!isFormValid) return;

    const from = toDateString(fromDate);
    const to = toDateString(toDate);

    if (from && to && new Date(from) > new Date(to)) {
      showToast('warn', 'Invalid Range', 'From Date cannot be greater than To Date');
      return;
    }

    // The audit trail only ever records what has already happened, so a future
    // date can only ever return nothing.
    const today = todayString();
    if (from > today || to > today) {
      showToast(
        'warn',
        'Future Date',
        'From Date and To Date cannot be in the future. Pick today or earlier.',
      );
      return;
    }

    setLoading(true);
    setPage(0);
    try {
      let combined;
      if (noChangeTypes) {
        // Nothing has ever been logged, so there is no type to query by and the
        // answer is known without a round trip.
        combined = [];
      } else if (changeType === 'ALL') {
        const requests = changeTypes.filter((ct) => ct !== 'ALL').map((ct) => getUpdateReports(from, to, ct));
        const responses = await Promise.all(requests);
        combined = responses.flat();
      } else {
        // A screen whose audit was written under more than one name is fetched
        // under every one of them, so picking either returns its full history.
        // Only the type the user actually picked is allowed to fail the search —
        // an alias that no longer exists in the table must not turn a good
        // result into an error.
        const [selected, ...aliases] = typesToQuery(changeType);
        const responses = await Promise.all([
          getUpdateReports(from, to, selected),
          ...aliases.map((ct) => getUpdateReports(from, to, ct).catch(() => [])),
        ]);
        combined = responses.flat().filter(Boolean);
      }

      setFilteredReports(
        combined.map((item) => ({
          ...item,
          oldData: tryParse(item.oldData),
          newData: tryParse(item.newData),
        })),
      );
      setHasSearched(true);
      setSearchedRange({ from, to });

      if (!combined.length) {
        showToast('info', 'No Data Found', noDetailsMessage(from, to));
      }
    } catch (err) {
      console.error(err);
      showToast('error', 'Load Failed', 'Could not load reports for the selected filters.');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (!filteredReports.length) {
      showToast('warn', 'No Data Found', 'No data to export');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Update Report');

    worksheet.columns = [
      { header: 'LOGIN ID', key: 'loginId', width: 20 },
      { header: 'LOGIN DATE', key: 'loginDate', width: 22 },
      { header: 'CHANGE TYPE', key: 'changeType', width: 20 },
      { header: 'OLD DATA', key: 'oldData', width: 50 },
      { header: 'NEW DATA', key: 'newData', width: 50 },
      { header: 'REASON', key: 'reason', width: 40 },
    ];

    filteredReports.forEach((report) => {
      worksheet.addRow({
        loginId: report.loginId,
        loginDate: report.loginDate,
        changeType: report.changeType,
        oldData: asText(report.oldData),
        newData: asText(report.newData),
        reason: report.reason,
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, name: 'Arial', size: 11, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF002FA7' } };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRow.height = 22;

    worksheet.eachRow((row, rowNum) => {
      if (rowNum > 1) {
        row.alignment = { vertical: 'top', wrapText: true };
        row.font = { name: 'Courier New', size: 10 };
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Update_Report.xlsx');
  };

  const exportToPdf = () => {
    if (!filteredReports.length) {
      showToast('warn', 'No Data Found', 'No data to export');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 47, 167);
    doc.text('Reports of Updates', 40, 30);
    doc.setTextColor(100);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 44);

    const columns = ['loginId', 'loginDate', 'changeType', 'oldData', 'newData', 'reason'];
    const headers = ['Login ID', 'Login Date', 'Change Type', 'Old Data', 'New Data', 'Reason'];

    const rows = filteredReports.map((report) => columns.map((col) => asText(report[col])));

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 56,
      styles: {
        font: 'courier', fontSize: 8, cellPadding: 5, overflow: 'linebreak',
        valign: 'top', lineColor: [226, 232, 240], lineWidth: 0.5,
      },
      headStyles: { fillColor: [0, 47, 167], textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
      alternateRowStyles: { fillColor: [248, 250, 255] },
      columnStyles: {
        0: { cellWidth: 70 }, 1: { cellWidth: 80 }, 2: { cellWidth: 75 },
        3: { cellWidth: 160 }, 4: { cellWidth: 160 }, 5: { cellWidth: 'auto' },
      },
      margin: { top: 56, left: 30, right: 30 },
      pageBreak: 'auto',
    });

    doc.save('Update_Report.pdf');
  };

  const exportData = () => {
    if (exportFormat === 'Excel') exportToExcel();
    else if (exportFormat === 'PDF') exportToPdf();
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc((v) => !v);
    else { setSortField(field); setSortAsc(true); }
  };

  const sortedReports = useMemo(() => {
    if (!sortField) return filteredReports;
    const rows = [...filteredReports];
    rows.sort((a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === 'loginDate') {
        av = new Date(av).getTime() || 0;
        bv = new Date(bv).getTime() || 0;
      } else {
        av = String(av ?? '').toLowerCase();
        bv = String(bv ?? '').toLowerCase();
      }
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filteredReports, sortField, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sortedReports.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = sortedReports
    .map((r, i) => ({ ...r, __index: i }))
    .slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const SortHeader = ({ field, label, style }) => (
    <th style={{ ...th, ...style, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        {sortField === field && (sortAsc ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </span>
    </th>
  );

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="rou-toast" />

      {/* ── Filter card ── */}
      <form
        onSubmit={filterReports}
        style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', marginBottom: '20px', position: 'relative' }}
        data-testid="rou-filter-form"
      >
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Audit Explorer</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Reports of Updates</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Filter audit activity and export the records you need</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }} htmlFor="rou-from-date">
              From Date <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              id="rou-from-date"
              type="date"
              className="input-field st-datepicker"
              style={{ fontSize: '13px', borderColor: touched && !fromDate ? '#DC2626' : undefined }}
              value={fromDate}
              max={todayString()}
              onChange={(e) => setFromDate(e.target.value)}
              {...pickerOnly()}
              data-testid="rou-from-date"
            />
            {touched && !fromDate && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }} htmlFor="rou-to-date">
              To Date <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              id="rou-to-date"
              type="date"
              className="input-field st-datepicker"
              style={{ fontSize: '13px', borderColor: touched && !toDate ? '#DC2626' : undefined }}
              value={toDate}
              max={todayString()}
              onChange={(e) => setToDate(e.target.value)}
              {...pickerOnly()}
              data-testid="rou-to-date"
            />
            {touched && !toDate && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Change Type {changeTypeRequired && <span style={{ color: '#DC2626' }}>*</span>}
            </label>
            <SearchableSelect
              options={changeTypeOptions}
              getLabel={(o) => o.label}
              getValue={(o) => o.value}
              value={changeType}
              onChange={(v) => setChangeType(v || '')}
              placeholder={noChangeTypes ? 'No change types yet' : 'Select type'}
              emptyMessage="No change types found"
              error={touched && changeTypeRequired && !changeType}
              testId="rou-change-type"
            />
            {touched && changeTypeRequired && !changeType && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Download Format</label>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '38px' }}>
              {['Excel', 'PDF'].map((fmt) => (
                <label key={fmt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#111827', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="rou-export-format"
                    value={fmt}
                    checked={exportFormat === fmt}
                    onChange={(e) => setExportFormat(e.target.value)}
                    data-testid={`rou-format-${fmt.toLowerCase()}`}
                  />
                  {fmt}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button type="submit" className="btn-primary" disabled={loading} data-testid="rou-get-records">
            <MagnifyingGlass size={14} style={{ marginRight: '4px' }} /> {loading ? 'Loading…' : 'Get Records'}
          </button>
          <button type="button" className="btn-secondary" onClick={exportData} disabled={!filteredReports.length} data-testid="rou-download">
            <DownloadSimple size={14} style={{ marginRight: '4px' }} /> Download
          </button>
        </div>
      </form>

      {/* ── Results card ── */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }} data-testid="rou-results">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Audit Records</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Live results from your selected filters</div>
          </div>
          {filteredReports.length > 0 && (
            <span style={{ fontSize: '10px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '3px 8px', borderRadius: '4px' }}>
              {filteredReports.length} records
            </span>
          )}
        </div>

        {filteredReports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px 24px', color: '#9CA3AF', fontSize: '13px' }} data-testid="rou-empty">
            <MagnifyingGlass size={28} style={{ marginBottom: '10px' }} />
            <div>
              {hasSearched
                ? noDetailsMessage(searchedRange?.from, searchedRange?.to)
                : <>Select filters and click <strong>Get Records</strong> to load data.</>}
            </div>
          </div>
        ) : (
          <>
            {/* width:0 + minWidth:100% is what actually makes this scroll. The
                page places this box in a `1fr` grid track, which defaults to
                min-width:auto and so stretches to the table's full width —
                overflowX alone then has nothing to overflow, and the right-hand
                columns end up off-screen with no scrollbar. A definite width of
                zero makes the intrinsic contribution zero while minWidth:100%
                still fills the track, so the table scrolls inside this box
                instead of widening it. Same fix as the Agent Code Change grid. */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '520px', width: 0, minWidth: '100%' }}>
              <table style={{ width: '100%', minWidth: '1180px', borderCollapse: 'collapse' }} data-testid="rou-table">
                <thead style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1 }}>
                  <tr>
                    <th style={{ ...th, width: '52px' }}>#</th>
                    <th style={{ ...th, minWidth: '140px' }}>Login ID</th>
                    <SortHeader field="loginDate" label="Login Date" style={{ minWidth: '160px' }} />
                    <SortHeader field="changeType" label="Change Type" style={{ minWidth: '150px' }} />
                    <th style={{ ...th, minWidth: '280px' }}>Old Data</th>
                    <th style={{ ...th, minWidth: '280px' }}>New Data</th>
                    <th style={{ ...th, minWidth: '200px' }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => {
                    const pal = paletteForType(row.changeType, changeTypes);
                    return (
                      <tr key={row.__index} style={{ background: row.__index % 2 ? '#FAFBFF' : 'white' }}>
                        <td style={{ ...td, color: '#9CA3AF', fontSize: '11px', fontWeight: '600' }}>{row.__index + 1}</td>
                        <td style={td}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#065F46', background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '2px 6px', borderRadius: '4px' }}>
                            {row.loginId}
                          </span>
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>{formatDatePart(row.loginDate)}</div>
                          <div style={{ color: '#9CA3AF', fontSize: '11px' }}>{formatTimePart(row.loginDate)}</div>
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: pal.text, background: pal.bg, border: `1px solid ${pal.border}`, padding: '2px 6px', borderRadius: '4px' }}>
                            {row.changeType}
                          </span>
                        </td>
                        <td style={td}><pre style={jsonCell}>{asText(row.oldData)}</pre></td>
                        <td style={td}><pre style={jsonCell}>{asText(row.newData)}</pre></td>
                        <td style={td}>{row.reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderTop: '1px solid #E5E7EB', fontSize: '12px', color: '#6B7280' }}>
              <div>
                Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, sortedReports.length)} of {sortedReports.length}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select
                  className="input-field"
                  style={{ fontSize: '12px', width: 'auto', padding: '4px 8px' }}
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  data-testid="rou-page-size"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <button type="button" className="btn-secondary" style={{ padding: '4px 8px' }} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                  <CaretLeft size={12} />
                </button>
                <span>{currentPage + 1} / {totalPages}</span>
                <button type="button" className="btn-secondary" style={{ padding: '4px 8px' }} disabled={currentPage >= totalPages - 1} onClick={() => setPage(currentPage + 1)}>
                  <CaretRight size={12} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ReportsOfUpdatesPage;
